import { useState, useEffect, useCallback } from 'react'
import { Loader2, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import apiClient from '@/lib/api/apiClient'
import { showSuccess, showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import { MentionTextarea, applyTaggingToNotes } from '@/components/notes/MentionTextarea'
import { EditNoteModal } from '@/components/notes/EditNoteModal'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { formatDateTime } from '../../utils/userManagementFormatters'
import {
  getNoteRowId,
  inputClass,
  isSystemNote,
} from '../../utils/editPageUi'
import notesStyles from '@/features/company/styles/libertyInternalNotes.module.css'
import type { NoteItem, UserNotesSectionProps } from '../../types'

export function UserNotesSection({
  userId,
  isLibertyRole,
  currentUserId,
  refreshSignal,
}: UserNotesSectionProps) {
  const [notes, setNotes] = useState<NoteItem[]>([])
  const [noteText, setNoteText] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [taggingContent, setTaggingContent] = useState<Record<string, string>>({})
  const [editingNote, setEditingNote] = useState<NoteItem | null>(null)

  const loadNotes = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<NoteItem[] | { data?: NoteItem[] }>(`/api/notes/${userId}`, {
        params: { notes_for: 'USER' },
      })
      const raw = res.data
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { data?: NoteItem[] }).data)
          ? (raw as { data: NoteItem[] }).data
          : []
      setNotes(list)
    } catch {
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void loadNotes()
  }, [loadNotes])

  useEffect(() => {
    const interval = setInterval(() => {
      void loadNotes()
    }, 30000)
    return () => clearInterval(interval)
  }, [loadNotes])

  // Force an immediate reload when the parent bumps refreshSignal (e.g. after a temp-password set).
  useEffect(() => {
    if (refreshSignal) void loadNotes()
  }, [refreshSignal, loadNotes])

  const handleAddNote = async () => {
    if (!noteText.trim()) return
    setSubmitting(true)
    try {
      const fd = new FormData()
      const notesToSend = applyTaggingToNotes(noteText.trim(), taggingContent ?? {})
      fd.append('notes', notesToSend)
      fd.append('notes_for', 'USER')
      await apiClient.post(`/api/notes/${userId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setNoteText('')
      setTaggingContent({})
      void loadNotes()
    } catch (err) {
      showValidationMessage(formatApiError(err), 'Note Error')
    } finally {
      setSubmitting(false)
    }
  }

  const canEditNote = (note: NoteItem): boolean => {
    if (!isLibertyRole) return false
    if (!currentUserId || String(note.fk_user_id) !== String(currentUserId)) return false
    if (isSystemNote(note.notes)) return false
    const createdTs = typeof note.created_at === 'number' ? note.created_at : Number(note.created_at)
    if (Number.isNaN(createdTs)) return false
    const nowSec = Math.floor(Date.now() / 1000)
    return nowSec - createdTs < 300
  }

  return (
    <div data-testid="um-edit-notes-section">
      <MentionTextarea
        className={cn(inputClass(), 'resize-none h-32 p-3 shadow-sm')}
        placeholder="Add a note… (use @ to mention Liberty users)"
        value={noteText}
        onChange={setNoteText}
        onTaggingAdd={(displayKey, useridToken) => {
          setTaggingContent((prev) => ({ ...prev, [displayKey]: useridToken }))
        }}
        disabled={submitting}
        dataTestId="um-edit-notes-add-input"
      />
      <button
        type="button"
        disabled={submitting || !noteText.trim()}
        onClick={() => void handleAddNote()}
        className={cn(primaryEntityActionButtonClass, 'mt-2.5 h-10 px-6 text-sm font-semibold rounded-xl')}
        data-testid="um-edit-notes-add-btn"
      >
        {submitting && <Loader2 className="h-3 w-3 shrink-0 animate-spin" />}
        Submit
      </button>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </div>
      )}

      <div className="mt-4 max-h-[32rem] overflow-y-auto pr-1" data-testid="um-edit-notes-list">
        {!loading && notes.length === 0 ? (
          <p className={cn(notesStyles.emptyState, 'text-center')} data-testid="um-edit-notes-empty">
            No notes yet.
          </p>
        ) : (
          <div className={notesStyles.notesList}>
            {notes.map((note, i) => {
              const noteId = note.notes_id ?? note.id ?? i
              const editableNoteId = getNoteRowId(note)
              const editable = canEditNote(note)

              return (
                <div
                  key={noteId}
                  className={notesStyles.noteRow}
                  data-testid={`um-edit-notes-item-${noteId}`}
                >
                  <div className={notesStyles.noteMeta}>
                    <div className={notesStyles.noteAuthor}>
                      {note.name}
                      {note.impersonated_by && (
                        <span className={notesStyles.noteImpersonated}>
                          {' '}
                          (impersonated by {note.impersonated_by})
                        </span>
                      )}
                    </div>
                    <div className={notesStyles.noteDate}>{formatDateTime(note.created_at)}</div>
                    {note.updated_at ? (
                      <div className={notesStyles.noteModified}>
                        Modified {formatDateTime(note.updated_at)}
                      </div>
                    ) : null}
                  </div>
                  <div className={notesStyles.noteContent}>
                    <p
                      className="m-0 whitespace-pre-wrap text-sm text-slate-800"
                      data-testid={`um-edit-notes-text-${noteId}`}
                    >
                      {note.notes}
                    </p>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => setEditingNote(note)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[rgb(21_151_255)] hover:text-[#0d7acc] transition-colors cursor-pointer focus:outline-none"
                        title="Edit note"
                        data-testid={`um-edit-notes-edit-btn-${editableNoteId ?? noteId}`}
                        disabled={editableNoteId == null}
                      >
                        <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>Edit</span>
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <EditNoteModal
        isOpen={!!editingNote}
        notesId={editingNote ? getNoteRowId(editingNote) : null}
        initialNotes={editingNote?.notes ?? ''}
        onClose={() => setEditingNote(null)}
        onSaved={() => {
          showSuccess('Note Edited Successfully!')
          void loadNotes()
        }}
        testIdPrefix="um-edit-notes-modal"
      />
    </div>
  )
}
